import { useEffect, useRef, useState } from 'react';

interface UseNotificationSoundReturn {
  playSound: () => Promise<void>;
  isEnabled: boolean;
  setEnabled: (enabled: boolean) => void;
  volume: number;
  setVolume: (volume: number) => void;
  isLoaded: boolean;
}

export const useNotificationSound = (): UseNotificationSoundReturn => {
  const [isEnabled, setIsEnabled] = useState(() => {
    const saved = localStorage.getItem('notification-sound-enabled');
    return saved !== null ? JSON.parse(saved) : true;
  });
  
  const [volume, setVolumeState] = useState(() => {
    const saved = localStorage.getItem('notification-sound-volume');
    return saved !== null ? parseFloat(saved) : 0.5;
  });
  
  const [isLoaded, setIsLoaded] = useState(false);
  const audioRefs = useRef<{ [key: string]: HTMLAudioElement }>({});

  // Precargar sonido único
  useEffect(() => {
    const audio = new Audio('/sounds/notification.mp3');
    audio.volume = volume;
    audio.preload = 'auto';
    
    audio.oncanplaythrough = () => {
      audioRefs.current.default = audio;
      setIsLoaded(true);
    };
    
    audio.onerror = () => {
      setIsLoaded(false);
    };

    return () => {
      Object.values(audioRefs.current).forEach(audio => {
        audio.src = '';
      });
      audioRefs.current = {};
    };
  }, []);

  // Actualizar volumen cuando cambie
  useEffect(() => {
    Object.values(audioRefs.current).forEach(audio => {
      audio.volume = volume;
    });
  }, [volume]);

  // Guardar preferencias en localStorage
  const setEnabled = (enabled: boolean) => {
    setIsEnabled(enabled);
    localStorage.setItem('notification-sound-enabled', JSON.stringify(enabled));
  };

  const setVolume = (newVolume: number) => {
    const clampedVolume = Math.max(0, Math.min(1, newVolume));
    setVolumeState(clampedVolume);
    localStorage.setItem('notification-sound-volume', clampedVolume.toString());
  };

  // Función para reproducir sonido único
  const playSound = async (): Promise<void> => {
    if (!isEnabled || !isLoaded) {
      return;
    }

    const audio = audioRefs.current.default;
    if (!audio) {
      return;
    }

    try {
      audio.currentTime = 0;
      await audio.play();
    } catch (error) {
      // Navegadores bloquean audio sin interacción del usuario
    }
  };

  return {
    playSound,
    isEnabled,
    setEnabled,
    volume,
    setVolume,
    isLoaded
  };
};